/**
 * TanStack Query hooks for Dashboard page
 *
 * This file demonstrates how Dashboard queries can be migrated from useManualQuery
 * to TanStack Query hooks for better performance and caching.
 *
 * Benefits:
 * - Automatic query deduplication (same queries won't fire multiple times)
 * - Built-in caching with configurable stale/cache times
 * - Better loading and error states
 * - Automatic background refetching
 */

import { useQueries, useQuery, UseQueryOptions } from '@tanstack/react-query'
import { useGraphQLQuery, useInvalidateQueries } from '../../../services/tanstack-query'
import { queryKeys } from '../../../services/tanstack-query/queryClient'
import {
  getHeatmapData,
  getLeavesByDateAndStatus,
  getPendingForApproverById,
  getUserIdsForApprover
} from '../../../graphql/custom-queries'
import dayjs from 'dayjs'
import { LeaveRequestStatus } from '@vacationtracker/shared/types/leave-request'
import { HeatmapEventType, IHeatmapHoliday } from '../../../components/heatmap-chart/types'
import { useMemo } from 'react'

export interface HeatmapDataProcessed {
  holidays: IHeatmapHoliday[]
  locations: Array<{ id: string; name: string }>
  departments: Array<{ id: string; name: string; parentId: string | null }>
  labels: Array<{ id: string; name: string; color: string }>
}

/**
 * Hook to fetch heatmap data (locations, departments, labels, holidays)
 *
 * This data is heavily cached (staleTime: 5 minutes) to reduce network requests
 * when users navigate back to the Dashboard.
 *
 * Returns processed data with holidays flattened and enriched with location info
 */
export function useHeatmapData() {
  const query = useGraphQLQuery<{
    locations: Array<{
      name: string
      id: string
      holidays: Array<{
        year: number
        holidays: Array<{
          name: string
          date: string
          isHalfDay: boolean
        }>
      }>
    }>
    departments: Array<{
      id: string
      name: string
      parentId: string | null
    }>
    labels: Array<{
      id: string
      name: string
      color: string
    }>
  }>(
    queryKeys.dashboard.all,
    getHeatmapData,
    undefined,
    {
      // Keep this data fresh for 5 minutes
      staleTime: 5 * 60 * 1000,
      // This query rarely changes, so we can cache it longer
      gcTime: 30 * 60 * 1000, // 30 minutes
    }
  )

  // Process the data to match Dashboard's expected format
  const processedData: HeatmapDataProcessed | undefined = useMemo(() => {
    if (!query.data) return undefined

    const holidays: IHeatmapHoliday[] = []
    const locations = query.data.locations || []

    if (locations.length > 0) {
      locations.forEach((location) => {
        location.holidays.forEach((holidayYear) => {
          holidays.push(
            ...holidayYear.holidays.map(holiday => ({
              ...holiday,
              heatmapType: HeatmapEventType.holiday,
              locationName: location.name,
              locationId: location.id,
            })) as IHeatmapHoliday[]
          )
        })
      })
    }

    return {
      holidays,
      locations: locations.map(location => ({ id: location.id, name: location.name })),
      departments: query.data.departments || [],
      labels: query.data.labels || [],
    }
  }, [query.data])

  return {
    ...query,
    data: processedData,
  }
}

/**
 * Hook to fetch leaves by date range and status
 *
 * @param startDate - Start date for the query
 * @param endDate - End date for the query
 * @param status - Leave request status (APPROVED, PENDING, etc.)
 * @param limit - Optional limit for pagination
 * @param nextToken - Optional pagination token
 */
export function useLeavesByDateAndStatus(
  startDate: Date,
  endDate: Date,
  status: LeaveRequestStatus = 'APPROVED',
  limit = 0,
  nextToken = 'NONE'
) {
  return useGraphQLQuery(
    [...queryKeys.leave.list(), startDate.toISOString(), endDate.toISOString(), status],
    getLeavesByDateAndStatus,
    {
      dateStart: dayjs(startDate).format('YYYY-MM-DD'),
      dateEnd: dayjs(endDate).format('YYYY-MM-DD'),
      status,
      limit,
      nextToken,
    },
    {
      // Keep data fresh for 2 minutes
      staleTime: 2 * 60 * 1000,
    }
  )
}

/**
 * Hook to fetch pending leave requests for a specific approver
 *
 * @param approverId - The approver's user ID
 */
export function usePendingForApprover(approverId: string) {
  return useGraphQLQuery(
    [...queryKeys.dashboard.pending, approverId],
    getPendingForApproverById,
    { approverId },
    {
      // Pending requests change frequently, so keep fresh for only 1 minute
      staleTime: 1 * 60 * 1000,
      // Enable automatic refetching on window focus for pending requests
      // This ensures approvers see the latest pending requests
      refetchOnWindowFocus: true,
    }
  )
}

/**
 * Hook to fetch user IDs for an approver
 *
 * This query determines which users' leave requests the approver can see
 */
export function useUserIdsForApprover() {
  return useGraphQLQuery(
    queryKeys.user.all,
    getUserIdsForApprover,
    undefined,
    {
      // This data changes rarely
      staleTime: 10 * 60 * 1000, // 10 minutes
    }
  )
}

/**
 * Hook to fetch all Dashboard data in parallel
 *
 * This uses useQueries to fetch multiple queries in parallel, which is more
 * efficient than fetching them sequentially.
 *
 * Benefits:
 * - Queries are deduped automatically
 * - All queries run in parallel
 * - Individual query results can be accessed separately
 * - Loading states are combined
 *
 * @example
 * const {
 *   heatmapData,
 *   upcomingLeaves,
 *   pendingLeaves,
 *   isLoading
 * } = useDashboardData('user-123')
 */
export function useDashboardData(approverId: string) {
  const startDate = new Date()
  const endDate = dayjs().add(6, 'months').toDate()

  const results = useQueries({
    queries: [
      {
        queryKey: queryKeys.dashboard.all,
        queryFn: async () => {
          const { graphqlRequest } = await import('../../../services/tanstack-query')
          return graphqlRequest(getHeatmapData)
        },
        staleTime: 5 * 60 * 1000,
      },
      {
        queryKey: [...queryKeys.leave.list(), startDate.toISOString(), endDate.toISOString(), 'APPROVED'],
        queryFn: async () => {
          const { graphqlRequest } = await import('../../../services/tanstack-query')
          return graphqlRequest(getLeavesByDateAndStatus, {
            dateStart: dayjs(startDate).format('YYYY-MM-DD'),
            dateEnd: dayjs(endDate).format('YYYY-MM-DD'),
            status: 'APPROVED',
            limit: 500,
            nextToken: 'NONE',
          })
        },
        staleTime: 2 * 60 * 1000,
      },
      {
        queryKey: [...queryKeys.dashboard.pending, approverId],
        queryFn: async () => {
          const { graphqlRequest } = await import('../../../services/tanstack-query')
          return graphqlRequest(getPendingForApproverById, { approverId })
        },
        staleTime: 1 * 60 * 1000,
        refetchOnWindowFocus: true,
      },
      {
        queryKey: queryKeys.user.all,
        queryFn: async () => {
          const { graphqlRequest } = await import('../../../services/tanstack-query')
          return graphqlRequest(getUserIdsForApprover)
        },
        staleTime: 10 * 60 * 1000,
      },
    ],
  })

  return {
    heatmapData: results[0].data,
    upcomingLeaves: results[1].data,
    pendingLeaves: results[2].data,
    userIds: results[3].data,
    isLoading: results.some((result) => result.isLoading),
    isError: results.some((result) => result.isError),
    errors: results.map((result) => result.error).filter(Boolean),
  }
}
